import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateSpeakerMut, type UpdateSpeakerInput } from '@/lib/api/monitoring/speakers';
import { UpdateRelayMut, type UpdateRelayInput } from '@/lib/api/monitoring/relays';
import { UpdateSecondaryDeviceMut } from '@/lib/api/devices/devices';
import { updateZone, type ZoneForm } from '../../../zonesApi';
import { DeviceData } from '../../DevicesTab';
import { MonitoringZoneDto, AssetVideoProcessorDto } from '@/lib/api/types.gen';

interface UseDeviceDropHandlersParams {
  monitoredCameras: any[];
  allSpeakers: DeviceData[];
  allRelays: DeviceData[];
  allCameras: any[]; // All cameras (monitored + unmonitored)
  site: any;
  companyId: string;
  siteId: string;
}

const getSiteDevicesQueryKey = (companyId: string, siteId: string) => {
  return ['siteDevices', { companyId, projectId: siteId }];
};

export const useDeviceDropHandlers = ({
  monitoredCameras,
  allSpeakers,
  allRelays,
  allCameras,
  site,
  companyId,
  siteId
}: UseDeviceDropHandlersParams) => {
  const queryClient = useQueryClient();
  const updateSpeakerMut = useMutation({
    ...UpdateSpeakerMut(),
  });
  const updateRelayMut = useMutation({
    ...UpdateRelayMut(),
  });

  const handleDeviceDrop = useCallback((deviceData: any, zoneId: string) => {
    
    // Handle multi-camera drops
    if (deviceData.deviceType === 'multi-camera') {
      // Group cameras by NVR to handle monitoring status updates per NVR
      const camerasGroupedByNVR = new Map<string, any[]>();
      
      deviceData.devices.forEach((camera: any) => {
        const nvrId = camera.nvrId || camera.parentDeviceId || camera.primaryDeviceId;
        if (nvrId) {
          if (!camerasGroupedByNVR.has(nvrId)) {
            camerasGroupedByNVR.set(nvrId, []);
          }
          camerasGroupedByNVR.get(nvrId)!.push(camera);
        }
      });

      // Process monitoring status updates for each NVR
      const monitoringUpdates = Array.from(camerasGroupedByNVR.entries()).map(async ([nvrId, nvrCameras]) => {
        
        // Find all cameras that belong to this NVR
        const camerasForThisNVR = allCameras.filter(cam => {
          const camNvrId = cam.nvrId || cam.parentDeviceId || cam.primaryDeviceId;
          return camNvrId === nvrId;
        });

        // Create updated devices array with monitored status for cameras being dropped
        const droppedCameraIds = new Set(nvrCameras.map(cam => cam.id));
        const updatedDevices = camerasForThisNVR.map(cam => ({
          id: cam.id,
          monitored: droppedCameraIds.has(cam.id) ? true : cam.monitored, // Set to true for dropped cameras
          description: cam.description,
          quillNotes: cam.quillNotes,
          capabilities: cam.capabilities,
          capabilitiesOverride: cam.capabilitiesOverride,
        }));

        const updateCameraInput = {
          primaryDeviceId: nvrId,
          devices: updatedDevices
        };

        // Use the mutation function directly
        const { mutationFn } = UpdateSecondaryDeviceMut(nvrId);
        await mutationFn(updateCameraInput);
      });

      // Handle zone assignment for all dropped cameras (if dropping on a non-default zone)
      let zoneUpdate = Promise.resolve();
      if (zoneId !== 'default') {
        try {
          const targetZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => zone.id === zoneId);
          
          if (targetZone) {
            const currentVideoProcessorIds = targetZone.videoProcessors?.map((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId) || [];
            const allDroppedCameraIds = deviceData.devices.map((camera: any) => camera.id);
            const newVideoProcessorIds = [...new Set([...currentVideoProcessorIds, ...allDroppedCameraIds])];
            
            const zoneUpdateInput: ZoneForm = {
              description: targetZone.description,
              videoProcessorIds: newVideoProcessorIds,
              schedule: targetZone.schedule,
            };

            zoneUpdate = updateZone(siteId, zoneId, zoneUpdateInput).then(() => {});
          }
        } catch (error) {
          console.error('❌ Zone update error:', error);
        }
      }

      // Wait for all updates to complete
      Promise.all([...monitoringUpdates, zoneUpdate]).then(() => {
        // Refresh both site data (zones) and device data
        return Promise.all([
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }),
          queryClient.refetchQueries({
            queryKey: ['site', siteId, companyId]
          })
        ]);
      }).catch((error) => {
        console.error('❌ Error in multi-camera updates:', error);
        queryClient.refetchQueries({ 
          queryKey: getSiteDevicesQueryKey(companyId, siteId)
        });
      });
      
      return; // Exit early for multi-camera drops
    }
    
    // Handle single camera drops (existing logic)
    if (deviceData.deviceType === 'camera') {
      // First, enable monitoring for the camera
      const camera = deviceData.device;
      const nvrId = camera.nvrId || camera.parentDeviceId || camera.primaryDeviceId;
      
      if (!nvrId) {
        return;
      }

      // Find all cameras that belong to the same NVR
      const camerasForThisNVR = allCameras.filter(cam => {
        const camNvrId = cam.nvrId || cam.parentDeviceId || cam.primaryDeviceId;
        return camNvrId === nvrId;
      });

      // Create updated devices array with all cameras for this NVR
      const updatedDevices = camerasForThisNVR.map(cam => {
        if (cam.id === camera.id) {
          // Update only the target camera to monitored: true
          return {
            id: cam.id,
            monitored: true,
            description: cam.description,
            quillNotes: cam.quillNotes,
            capabilities: cam.capabilities,
            capabilitiesOverride: cam.capabilitiesOverride,
          };
        } else {
          // Preserve existing monitored state for all other cameras
          return {
            id: cam.id,
            monitored: cam.monitored, // Keep existing monitored state
            description: cam.description,
            quillNotes: cam.quillNotes,
            capabilities: cam.capabilities,
            capabilitiesOverride: cam.capabilitiesOverride,
          };
        }
      });

      const updateCameraInput = {
        primaryDeviceId: nvrId,
        devices: updatedDevices
      };

      // Use the mutation function directly  
      const { mutationFn } = UpdateSecondaryDeviceMut(nvrId);
      mutationFn(updateCameraInput).then(() => {
        // Handle zone assignment (only if we're not dropping on default zone)
        if (zoneId !== 'default') {
          try {
            const targetZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => zone.id === zoneId);
            
            if (targetZone) {
              // Add this camera to the zone
              const currentVideoProcessorIds = targetZone.videoProcessors?.map((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId) || [];
              const newVideoProcessorIds = [...new Set([...currentVideoProcessorIds, camera.id])];
              
              const zoneUpdateInput: ZoneForm = {
                description: targetZone.description,
                videoProcessorIds: newVideoProcessorIds,
                schedule: targetZone.schedule,
              };
              
              updateZone(siteId, zoneId, zoneUpdateInput).then(() => {
                // Refresh queries after zone update
                queryClient.refetchQueries({ 
                  queryKey: getSiteDevicesQueryKey(companyId, siteId)
                });
                queryClient.refetchQueries({
                  queryKey: ['site', siteId, companyId]
                });
              });
            } else {
              console.warn('🎯 Target zone not found', { zoneId });
            }
          } catch (error) {
            console.error('🎯 Zone update failed', error);
          }
        } else {
          // Refresh queries for default zone drops
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          });
          queryClient.refetchQueries({
            queryKey: ['site', siteId, companyId]
          });
        }
      });
      
      return; // Exit early for camera drops
    }

    // Existing logic for speakers/relays...
  }, [monitoredCameras, allSpeakers, allRelays, allCameras, site, siteId, companyId, queryClient]);

  const handleSingleCameraDrop = useCallback((cameraData: any, zoneId: string) => {
    // Handle single camera drop (similar to multi-camera but for single device)
    const deviceData = {
      deviceType: 'camera' as const,
      device: cameraData,
    };
    
    handleDeviceDrop(deviceData, zoneId);
  }, [handleDeviceDrop]);

  return {
    handleDeviceDrop,
    handleSingleCameraDrop,
  };
};